import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, HardHat, Info, Package, PlayCircle } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import { useApplicationMutations, useParcels, useWpwPackages } from "../hooks";
import type { ParcelOwner, PowerProject } from "../types";
import { isWpwCandidateParcel } from "../utils";
import { SectionCard } from "./ui";
import { WpwHandoverPanel } from "./WpwHandoverPanel";
import { WpwMassLinks } from "./WpwMassLinks";
import { WpwRequisitionModal } from "./WpwRequisitionModal";

// Czym jest WNU — pytanie wraca przy każdym wdrożeniu, więc odpowiedź stoi przy
// przycisku, a nie w instrukcji. Treść jedna dla nagłówka sekcji i dla wiersza właściciela.
const WNU_INFO =
  "WNU to numer wniosku urzędowego (WNU/rok/kolejny numer) — tak system oznacza pakiet " +
  "dokumentów prowadzony kreatorem wniosków. Przy WPW pakiet jest PER WŁAŚCICIEL: każdy " +
  "współwłaściciel działki ma własny WNU, własne formularze ENERGA i własny komplet do " +
  "podpisu; mapa i wypis są wspólne dla działki. Małżonkowie ze wspólnością ustawową " +
  "podpisują jeden komplet, ale każde z nich jest osobnym właścicielem — i ma osobny WNU.";

function WnuInfo({ context }: { context?: string }) {
  return (
    <Tooltip content={context ? `${context}\n\n${WNU_INFO}` : WNU_INFO} className="inline-flex">
      <span
        role="note"
        tabIndex={0}
        aria-label="Co to jest WNU"
        className="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-primary)] cursor-help"
      >
        <Info size={12} />
      </span>
    </Tooltip>
  );
}

// Udział właściciela w działce — dokładnie to, co pokazuje karta właściciela przy
// wypisie/KW: udział ze wspólności, udział samodzielny obok niej i współmałżonek
// (wspólność = jeden podpis, ale osobne WNU dla każdego z małżonków).
function OwnerShare({ owner, siblings }: { owner: ParcelOwner; siblings: ParcelOwner[] }) {
  const spouses = siblings.filter(
    (s) => s.id !== owner.id && !!owner.marital_group && s.marital_group === owner.marital_group,
  );
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-[10px] text-[var(--color-text-muted)]">
      {owner.ownership_share ? (
        <span title="Udział w działce według wypisu / KW">udział {owner.ownership_share}</span>
      ) : (
        <span className="italic" title="Wypis nie podaje udziału tego właściciela">
          udział nieznany
        </span>
      )}
      {owner.own_share && (
        <span
          className="cursor-help"
          title="Udział trzymany samodzielnie, obok udziału we wspólności małżeńskiej — jeden WNU z oboma udziałami"
        >
          + {owner.own_share} samodzielnie
        </span>
      )}
      {spouses.length > 0 && (
        <span
          className="text-rose-700 dark:text-rose-400 cursor-help"
          title="Wspólność małżeńska według wypisu — wspólny udział i jeden podpis, ale każdy małżonek ma własny WNU"
        >
          ⚭ {spouses.map((s) => s.full_name).join(", ")}
        </span>
      )}
    </span>
  );
}

/**
 * Zakładka WPW — CAŁY obieg zgód prywatnych w kolejności pracy:
 * 1) pakiety ENERGA per działka (utwórz → wypełnij fazy → skompiluj),
 * 2) zapotrzebowanie (podwykonawca, pozycje, typ, termin) — skompilowane pakiety
 *    wchodzą jako komplety wejściowe AUTOMATYCZNIE; tworzy się TYLKO tutaj,
 * 3) zlecenia WPW w toku (masowa generacja / zwroty),
 * 4) przekazania per właściciel.
 */
export function WpwMasoweTab({ connection }: { connection: PowerProject }) {
  const { data: parcels } = useParcels(connection.id);
  // „pokaż nieaktualnych” — pakiety WNU właścicieli, których ponowny wypis nie potwierdził
  // (domyślnie schowane; zostają jako historia).
  const [showOutdated, setShowOutdated] = useState(false);
  const { data: packages } = useWpwPackages(connection.id, true, showOutdated);
  const m = useApplicationMutations();
  const navigate = useNavigate();
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  // Właściciel, na którego przycisku „Załóż WNU" stoi kursor — podświetlamy wiersz i przycisk.
  const [hoverOwner, setHoverOwner] = useState<string | null>(null);
  const [err, setErr] = useState("");

  // Działki prywatne wymagające pozyskania zgody — JEDNA reguła (utils.isWpwCandidateParcel),
  // ta sama co w pickerze zapotrzebowania i w backendowym `_private_parcels`: prywatna
  // (właściciel bez urzędu ALBO kod własności 7/15), pod aktualną trasą (albo ręczna),
  // WYKORZYSTYWANA w projekcie i w trybie „wymaga pozyskania".
  const privateParcels = useMemo(
    () => (parcels ?? []).filter(isWpwCandidateParcel),
    [parcels],
  );

  // Pakiet WPW jest per WŁAŚCICIEL → lookup po owner_id.
  const packagesByOwner = useMemo(() => {
    const map = new Map<string, NonNullable<typeof packages>[number]>();
    for (const p of packages ?? []) if (p.owner_id) map.set(p.owner_id, p);
    return map;
  }, [packages]);
  // Pakiety właścicieli NIEAKTUALNYCH per działka (tylko gdy checkbox włączony) — nie
  // mają wiersza w `p.owners` (karta działki ich nie niesie), więc idą osobną listą.
  const outdatedByParcel = useMemo(() => {
    const map = new Map<string, NonNullable<typeof packages>>();
    for (const pkg of packages ?? []) {
      if (!pkg.owner_outdated) continue;
      map.set(pkg.parcel_id, [...(map.get(pkg.parcel_id) ?? []), pkg]);
    }
    return map;
  }, [packages]);

  const createPackage = (parcelId: string, ownerId: string) => {
    setErr("");
    setCreatingFor(ownerId);
    m.create.mutate(
      { application_type: "wpw_pakiet", connection: connection.id, wpw_parcel: parcelId, wpw_owner: ownerId },
      {
        onSuccess: (app) => navigate(`/power-projects/wnioski/${app.id}`),
        onError: () => setErr("Nie udało się utworzyć WNU — sprawdź, czy właściciel nie ma już aktywnego."),
        onSettled: () => setCreatingFor(null),
      },
    );
  };

  // Sekwencja masowa WPW: przenosi do NADZOROWANEJ strony krok po kroku
  // (pakiety → opisy AI → formularze → kompilacja → utwórz zlecenie).
  const goToSequence = () => navigate(`/power-projects/${connection.project}/wpw-sekwencja`);

  return (
    <div className="space-y-4">
      {/* Pakiety, zapotrzebowanie i zlecenia w toku startują zwinięte — zakładka otwiera się
          na przekazaniach per właściciel, reszta rozwija się kliknięciem w nagłówek. */}
      <SectionCard
        collapsible
        defaultCollapsed
        title={
          <span className="flex items-center gap-2">
            <Package size={14} /> Pakiety ENERGA per działka (komplety wejściowe dla negocjatora)
            <WnuInfo />
          </span>
        }
      >
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <p className="text-[11px] text-[var(--color-text-muted)] flex-1">
            Jeden przycisk otwiera nadzorowaną sekwencję krok po kroku dla wszystkich działek
            prywatnych: utwórz pakiety → opisy AI → formularze ENERGA → kompilacja → <b>utwórz
            zlecenie (na końcu, tam)</b> albo zakończ bez zamówienia i dołącz pakiety do
            istniejącego zlecenia wnioskiem o zmianę. Każdym krokiem sterujesz i widzisz jego log.
          </p>
          <label
            className="flex cursor-pointer select-none items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]"
            title="Pakiety WNU właścicieli oznaczonych jako nieaktualni po ponownie zamówionym wypisie — zostają jako historia, domyślnie schowane."
          >
            <input
              type="checkbox"
              className="accent-[var(--color-primary)] cursor-pointer"
              checked={showOutdated}
              onChange={(e) => setShowOutdated(e.target.checked)}
            />
            pokaż nieaktualnych właścicieli
          </label>
          {privateParcels.length > 0 && (
            <Button onClick={goToSequence}
                    title="Otwiera nadzorowaną sekwencję masową WPW: przechodzisz kroki po kolei, a na końcu tworzysz zlecenie z tego, co powstało.">
              <PlayCircle size={15} className="mr-1" /> Sekwencja masowa WPW ({privateParcels.length}) →
            </Button>
          )}
        </div>
        {privateParcels.length === 0 ? (
          <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
            Brak działek prywatnych wymagających pozyskania zgody.
          </p>
        ) : (
          <div className="divide-y divide-[var(--color-border-muted)] border border-[var(--color-border)]">
            {privateParcels.map((p) => {
              const owners = (p.owners ?? []).filter((o) => !o.office);
              return (
                <div key={p.id} className="px-3 py-2 text-[11px] font-mono">
                  <div className="text-[var(--color-text-primary)] font-semibold mb-1">
                    {p.teryt}
                    {owners.length > 1 && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[9px] text-amber-700">
                        · {owners.length} WNU (per właściciel) <WnuInfo />
                      </span>
                    )}
                  </div>
                  {owners.length === 0 && (
                    // Działka jest prywatna z KODU WŁASNOŚCI, ale nie ma komu założyć WNU.
                    // Dwa różne powody = dwie różne naprawy, więc mówimy który zachodzi.
                    <div className="text-[var(--color-text-muted)]">
                      {(p.owners ?? []).length === 0
                        ? "brak właściciela — dodaj adresata WPW w danych działki"
                        : `wszyscy właściciele mają przypisany urząd, a kod własności ${p.ownership_code} wskazuje działkę prywatną — popraw właściciela (zdejmij urząd) albo kod własności`}
                    </div>
                  )}
                  {showOutdated && (outdatedByParcel.get(p.id) ?? []).map((pkg) => (
                    <div
                      key={pkg.application_id}
                      className="flex flex-wrap items-center gap-2 -mx-1 px-1 py-0.5 opacity-70"
                      title={pkg.owner_outdated_reason || "Właściciel nieaktualny — ponowny wypis go nie potwierdził."}
                    >
                      <span className="flex-1 min-w-0 truncate line-through text-[var(--color-text-muted)]">
                        {pkg.owner_name || "—"}
                      </span>
                      <Badge color="gray">nieaktualny</Badge>
                      <Link className="text-[var(--color-primary)] hover:underline"
                            to={`/power-projects/wnioski/${pkg.application_id}`}>
                        {pkg.number} →
                      </Link>
                    </div>
                  ))}
                  <div className="space-y-1">
                    {owners.map((o) => {
                      const pkg = packagesByOwner.get(o.id);
                      // Najechanie na akcję WNU (załóż / dokończ / otwórz) podświetla TEGO
                      // właściciela — przy kilku współwłaścicielach na jednej działce inaczej
                      // nie widać, kogo dotyczy klik. Jeden zestaw handlerów dla wszystkich
                      // trzech wariantów wiersza, bo każdy prowadzi do WNU tej samej osoby.
                      const aimed = hoverOwner === o.id;
                      const aimHandlers = {
                        onMouseEnter: () => setHoverOwner(o.id),
                        onMouseLeave: () => setHoverOwner(null),
                        onFocus: () => setHoverOwner(o.id),
                        onBlur: () => setHoverOwner(null),
                      };
                      const wnuLinkClass = `text-[var(--color-primary)] hover:underline ${
                        aimed ? "underline font-semibold" : ""
                      }`;
                      return (
                        <div
                          key={o.id}
                          className={`flex flex-wrap items-center gap-2 -mx-1 px-1 py-0.5 rounded transition-colors ${
                            aimed
                              ? "bg-[var(--color-primary-dim)] ring-1 ring-[var(--color-primary)]"
                              : ""
                          }`}
                        >
                          <span className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2">
                            <span className="truncate text-[var(--color-text-secondary)]">{o.full_name || "—"}</span>
                            <OwnerShare owner={o} siblings={owners} />
                          </span>
                          {pkg?.quality?.ready ? (
                            <>
                              <Badge color="green">
                                <CheckCircle2 size={11} className="mr-1 inline" /> sprawdzony
                              </Badge>
                              <Link className={wnuLinkClass}
                                    title={`Otwórz pakiet WPW (WNU) właściciela: ${o.full_name || "—"}`}
                                    {...aimHandlers}
                                    to={`/power-projects/wnioski/${pkg.application_id}`}>
                                {pkg.number} →
                              </Link>
                              <WnuInfo context={`${pkg.number} — pakiet WPW właściciela: ${o.full_name || "—"} (dz. ${p.teryt}).`} />
                            </>
                          ) : pkg ? (
                            <>
                              <span title={pkg.quality?.reason}>
                                <Badge color="yellow">{pkg.compiled ? "wymaga kontroli" : "w przygotowaniu"}</Badge>
                              </span>
                              <Link className={wnuLinkClass}
                                    title={`Dokończ pakiet WPW (WNU) właściciela: ${o.full_name || "—"}`}
                                    {...aimHandlers}
                                    to={`/power-projects/wnioski/${pkg.application_id}`}>
                                dokończ {pkg.number} →
                              </Link>
                              <WnuInfo context={`${pkg.number} — pakiet WPW właściciela: ${o.full_name || "—"} (dz. ${p.teryt}).`} />
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="secondary"
                                      loading={creatingFor === o.id}
                                      className={aimed ? "border-[var(--color-primary)] text-[var(--color-primary)]" : ""}
                                      title={`Załóż pakiet WPW (WNU) dla: ${o.full_name || "—"}`}
                                      {...aimHandlers}
                                      onClick={() => createPackage(p.id, o.id)}>
                                Załóż WNU
                              </Button>
                              <WnuInfo context={`Nowy WNU powstanie dla właściciela: ${o.full_name || "—"} (dz. ${p.teryt}).`} />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {err && <p className="text-[11px] text-red-600 mt-2">{err}</p>}
      </SectionCard>

      <SectionCard
        collapsible
        defaultCollapsed
        title={
          <span className="flex items-center gap-2">
            <HardHat size={14} /> Zapotrzebowanie — zgody prywatne (WPW)
          </span>
        }
      >
        <WpwRequisitionModal
          inline
          connectionId={connection.id}
          projectId={connection.project}
          parcels={parcels ?? []}
          onClose={() => {}}
        />
      </SectionCard>

      {/* Zlecenia WPW w toku — wejścia do masowej generacji / zwrotów. */}
      <WpwMassLinks connectionId={connection.id} projectId={connection.project} collapsible />

      <WpwHandoverPanel connectionId={connection.id} title="Przekazania WPW (per właściciel)" scrollList />
    </div>
  );
}
